<template>
    <div class="table-responsive">
        <!-- <h5>Notes</h5> -->
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list" id="table-notes">
            <b-thead>
                <b-tr>
                    <b-th style="width: 30px;"></b-th>
                    <b-th>Title:</b-th>
                    <b-th>Priority:</b-th>
                    <b-th>Notification:</b-th>
                    <b-th>Note:</b-th>
                    <b-th>Added:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody v-if="notes.length === 0">
                <b-tr>
                    <td colspan="7">
                        <i>No records available</i>
                    </td>
                </b-tr>
            </b-tbody>
            <draggable
                v-else
                v-model="notes"
                tag="tbody"
                ghost-class="sort-row-ghost"
                drag-class="sort-row-drag"
                :animation="0"
                handle=".handle"
                @start="isRowDragging = true"
                @end="isRowDragging = false"
                @sort="notesSorted"
            >
                <tr v-for="(note, i) in notes" :key="i">
                    <td style="cursor: move;" class="handle">
                        <i class="cus-black-white-swatch" />
                    </td>
                    <b-td>
                        <input type="hidden" name="id" :value="note.id" />
                        <strong
                            v-if="
                                note.isEditable &&
                                note.isEditable.status &&
                                _hasAccess('patient.precaution.update')
                            "
                            class="text-primary cursor-pointer"
                            @click="editNote(note)"
                        >
                            {{ note.content }}
                        </strong>
                        <span v-else>{{ note.content }}</span>
                    </b-td>
                    <b-td>{{ getPriorityLabel(note.priority) }}</b-td>
                    <b-td>{{ note.notificationLabel }}</b-td>
                    <b-td>{{ note.note }}</b-td>
                    <b-td>
                        <span v-if="note.create_at">{{
                            _setDateDisplay(note.create_at)
                        }}</span>
                        <span v-else>--</span>
                    </b-td>
                    <b-td class="text-right">
                        <b-dropdown
                            variant="link"
                            toggle-class="text-decoration-none"
                            no-caret
                            v-if="
                                note.isEditable &&
                                note.isEditable.status &&
                                (_hasAccess('patient.precaution.update') ||
                                    _hasAccess('patient.precaution.delete'))
                            "
                            right
                            lazy
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>

                            <b-dropdown-item
                                v-if="_hasAccess('patient.precaution.update')"
                                href="#"
                                @click="editNote(note)"
                            >
                                <i class="cus-page-white-edit"></i>
                                Edit Note
                            </b-dropdown-item>
                            <b-dropdown-item
                                v-if="_hasAccess('patient.precaution.delete')"
                                href="#"
                                @click="confirmDelete(note)"
                            >
                                <i class="cus-delete"></i>
                                Delete Note
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-td>
                </tr>
            </draggable>
        </b-table-simple>
        <b-pagination
            v-if="meta"
            v-show="meta.lastPage > 1"
            class="vet-pagination"
            align="right"
            v-model="meta.currentPage"
            :total-rows="meta.total"
            :per-page="meta.perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="getNoteList"
        />

        <modal-delete
            ref="modalDeleteNote"
            @reload="getNoteList()"
            :patientId="patientId"
        />

        <modal-edit-note
            @reload="getNoteList()"
            ref="modalEditNote"
            :patientId="patientId"
        />

        <modal-sort
            ref="modalSort"
            @saved="saveNoteSorting()"
            @cancel="cancelSort()"
        />
    </div>
</template>
<script>
import Notes from "@/services/patient-notes";
import Loader from "@/components/elements/loader-list";
import { cloneDeep, filter } from "lodash";
import draggable from "vuedraggable";

export default {
    components: {
        draggable,
        Loader,
        ModalDelete: () => import("@/components/patient-notes/note-delete"),
        ModalEditNote: () => import("@/components/patient-notes/note-save"),
        ModalSort: () => import("@/components/patient-notes/modal-sort")
    },
    props: {
        patientId: {
            type: String
        }
    },
    data() {
        return {
            notes: [],
            originalNotes: [],
            isLoading: false,
            showTheToast: false,
            selectedNote: {
                title: null,
                note: null,
                notification: { value: 0 }
            },
            meta: null,
            isRowDragging: false
        };
    },
    computed: {
        activeNotes: function () {
            return filter(this.notes, function (note) {
                return note.isactive;
            });
        }
    },
    mounted() {
        this.getNoteList();
    },
    methods: {
        async notesSorted(e) {
            const newIndex = e.newDraggableIndex;
            const oldIndex = e.oldDraggableIndex;
            let note = this.notes[newIndex];

            if (
                newIndex !== null &&
                ((newIndex < oldIndex &&
                    this.notes[newIndex].priority !==
                        this.notes[newIndex + 1].priority) ||
                    (newIndex > oldIndex &&
                        this.notes[newIndex].priority !==
                            this.notes[newIndex - 1].priority))
            ) {
                // if the note is moved up and the new priority is less than the original priority, set the new priority to the original priority
                // or if the note is moved down and the new priority is greater than the original priority, set the new priority to the original priority
                if (
                    newIndex < oldIndex &&
                    note.priority < this.originalNotes[newIndex].priority
                ) {
                    note.priority = this.originalNotes[newIndex].priority;
                    this.confirmSort(note, "up", note.priority);
                    return;
                } else if (
                    newIndex > oldIndex &&
                    note.priority > this.originalNotes[newIndex].priority
                ) {
                    note.priority = this.originalNotes[newIndex].priority;
                    this.confirmSort(note, "down", note.priority);
                    return;
                }
            }

            this.saveNoteSorting();
        },
        async saveNoteSorting() {
            try {
                // update popup ui
                let popupNotes = this.notes.filter(n => {
                    if (n.notification === 1) {
                        // 1 = popup
                        return true;
                    } else {
                        return false;
                    }
                });

                this.$store.dispatch(
                    "patient/refreshSortPopupNotes",
                    popupNotes
                );

                let sortedIds = [];

                // prepare for server param
                for (let note of this.notes) {
                    sortedIds.push(note.id);
                }

                // save to server
                await this.$http.post(
                    `${process.env.BARRAMUNDI}patient/${this.patientId}/reorder-notes`,
                    {
                        precaution_ids: sortedIds
                    }
                );

                this.originalNotes = cloneDeep(this.notes);
            } catch (error) {
                console.log(error);
            }
        },
        cancelSort() {
            this.notes = cloneDeep(this.originalNotes);
        },
        getNoteList(page) {
            this.isLoading = true;
            let params = {
                page: page ? page : 1
            };
            Notes.getNotes(this.patientId, params)
                .then(response => {
                    this.notes = response.resource;
                    this.originalNotes = cloneDeep(this.notes);
                    this.meta = response.meta;
                    this.isLoading = false;
                })
                .catch(error => {
                    this.showError();
                    console.error(error);
                });
        },
        showError() {
            this._message("Error : Unable to get notes", "danger");
            this.isLoading = false;
        },
        getPriorityLabel(priority) {
            let label = "Low";
            if (priority) {
                if (priority == 1) {
                    label = "Medium";
                } else if (priority == 2) {
                    label = "High";
                }
            }

            return label;
        },
        confirmSort(note, direction) {
            this.$refs.modalSort.open(note, direction);
        },
        confirmDelete(note) {
            this.$refs.modalDeleteNote.open(note.id);
        },
        editNote(note) {
            this.$refs.modalEditNote.open(note);
        }
    }
};
</script>
